import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PendingApproval {
  id: string;
  entityType: 'APPOINTMENT' | 'SCHEDULED_ACTIVITY';
  entityId: string;
  entityTitle: string;
  entityDate?: string;
  createdBy: { id: string; name: string };
  responses: Array<{
    userId: string;
    status: 'APPROVED' | 'DECLINED';
    user: { id: string; name: string };
  }>;
  createdAt: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatEntityType(type: string) {
  if (type === 'APPOINTMENT') return 'Appointment';
  if (type === 'SCHEDULED_ACTIVITY') return 'Activity';
  return type;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── NotificationsPage ────────────────────────────────────────────────────────

export function NotificationsPage() {
  const navigate = useNavigate();

  const [familyId, setFamilyId] = useState('');
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadApprovals = useCallback(async (fid: string) => {
    try {
      const data = await api.getPendingApprovals(fid);
      setApprovals(data as PendingApproval[]);
    } catch {
      // Not an owner or no approvals — clear list silently
      setApprovals([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const families = await api.getFamilies();
        if (!families.length) { navigate('/onboarding'); return; }
        const fid = families[0].id;
        setFamilyId(fid);
        await loadApprovals(fid);
      } catch {
        setError('Unable to load notifications. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, loadApprovals]);

  const handleRespond = async (requestId: string, status: 'APPROVED' | 'DECLINED') => {
    setActionLoading(requestId + status);
    try {
      await api.respondToApproval(familyId, requestId, status);
      await loadApprovals(familyId);
    } catch (err: any) {
      alert('Failed to respond: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-xl font-semibold text-gray-900 mb-6">Notifications</h1>

      {/* ── Pending approvals ─────────────────────────────────────────── */}
      <section className="mb-8">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
          Pending approvals
        </h2>

        {approvals.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 px-5 py-10 text-center">
            <svg className="w-8 h-8 text-gray-200 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <p className="text-sm text-gray-400">No pending approvals</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {approvals.map((approval) => {
              const approving = actionLoading === approval.id + 'APPROVED';
              const declining = actionLoading === approval.id + 'DECLINED';
              const busy = !!actionLoading;

              return (
                <li key={approval.id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <span className={`mt-0.5 shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${
                      approval.entityType === 'APPOINTMENT'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {formatEntityType(approval.entityType)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{approval.entityTitle}</p>
                      {approval.entityDate && (
                        <p className="text-xs text-gray-500 mt-0.5">{formatDate(approval.entityDate)}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Added by {approval.createdBy.name} · {timeAgo(approval.createdAt)}
                      </p>
                      {approval.responses.length > 0 && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {approval.responses.map((r) => r.user.name).join(', ')} approved
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleRespond(approval.id, 'APPROVED')}
                      disabled={busy}
                      className="flex-1 py-2 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60 transition-colors"
                    >
                      {approving ? 'Approving…' : 'Approve'}
                    </button>
                    <button
                      onClick={() => handleRespond(approval.id, 'DECLINED')}
                      disabled={busy}
                      className="flex-1 py-2 text-xs font-medium rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-60 transition-colors"
                    >
                      {declining ? 'Declining…' : 'Decline'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ── History ───────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
          History
        </h2>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-8 text-center">
          <p className="text-sm text-gray-400">Full notification history coming soon.</p>
        </div>
      </section>
    </div>
  );
}
